import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Check, CheckCheck, X, Shield, AlertTriangle, Lock, Globe, Key, Bug, Server, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function getHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('tl_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

const ICONS = {
  scan_complete: Shield,
  new_vulnerability: Bug,
  ssl_expiring: Lock,
  new_asset: Globe,
  domain_verified: Check,
  team_invite: Globe,
  misconfig_found: AlertTriangle,
  secret_found: Key,
};

const SEVERITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  info: 'bg-teal-500',
  medium: 'bg-amber-500',
  low: 'bg-gray-400',
};

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const resp = await fetch(`${API_URL}/api/notifications?limit=20`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markRead = async (id) => {
    await fetch(`${API_URL}/api/notifications/${id}/read`, { method: 'POST', headers: getHeaders() });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await fetch(`${API_URL}/api/notifications/read-all`, { method: 'POST', headers: getHeaders() });
    fetchNotifications();
  };

  const deleteNotif = async (id) => {
    await fetch(`${API_URL}/api/notifications/${id}`, { method: 'DELETE', headers: getHeaders() });
    fetchNotifications();
  };

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-center">
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-lg hover:bg-gray-100"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center min-w-[18px] px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1" data-testid="mark-all-read">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => {
                const Icon = ICONS[n.type] || Bell;
                const dotColor = SEVERITY_COLORS[n.severity] || 'bg-gray-400';
                return (
                  <div
                    key={n.notification_id}
                    className={`px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-teal-50/30' : ''}`}
                    data-testid={`notification-${n.notification_id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      <div className={`w-7 h-7 rounded-lg ${n.read ? 'bg-gray-100' : 'bg-teal-100'} flex items-center justify-center`}>
                        <Icon className={`w-3.5 h-3.5 ${n.read ? 'text-gray-400' : 'text-teal-600'}`} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                        <p className={`text-xs font-semibold truncate ${n.read ? 'text-gray-500' : 'text-gray-900'}`}>{n.title}</p>
                      </div>
                      <p className="text-xs text-gray-500 line-clamp-2">{n.message}</p>
                      <span className="text-xs text-gray-400 mt-1 block">{timeAgo(n.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!n.read && (
                        <button onClick={() => markRead(n.notification_id)} className="p-1 hover:bg-gray-200 rounded" title="Mark read">
                          <Check className="w-3 h-3 text-gray-400" />
                        </button>
                      )}
                      <button onClick={() => deleteNotif(n.notification_id)} className="p-1 hover:bg-gray-200 rounded" title="Dismiss">
                        <X className="w-3 h-3 text-gray-400" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
